<template>
  <flow-layout :current-step-index="isBrokerFlow ? 2 : 1" class="fundingNeeds">
    <heading level="2">{{ isBrokerFlow ? t('brokerTitle') : t('title') }}</heading>
    <form autocomplete="off">
      <div class="fundingNeedsAmount">
        <p class="fundingNeedsTitle">
          {{ isBrokerFlow ? t('desiredLoanAmount.brokerLabel') : t('desiredLoanAmount.label') }}
        </p>
        <input-inline>
          <input-money
            id="desired-loan-amount"
            name="desired-loan-amount"
            class="desiredLoanInput"
            @input="checkFormValidation"
            :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
            :maxlength="FUNDING_NEEDS_DESIRED_LOAN_AMOUNT_MAX_LENGTH"
            v-model="application.requestedAmount"
            :placeholder="t('desiredLoanAmount.placeholder')"
            v-validation="[['dynamic', '/requestedAmount']]"
          >
            <template #prefix>{{ $t('currency.dollar') }}</template>
          </input-money>
        </input-inline>
      </div>

      <div class="fundingNeedsPurpose">
        <p class="fundingNeedsTitle">
          {{ isBrokerFlow ? t('brokerFundingGroup') : t('fundingGroup') }}
        </p>
        <p class="fundingNeedsSubtitle">{{ t('fundingGroupHelper') }}</p>
        <visual-picker-group
          id="plan-to-use-funds"
          class="fundingGroup"
          @change="checkFormValidation"
          v-validation:changed="['requiredFunding']"
          v-model="application.fundingPurposes"
          :options="fundsOptions"
        ></visual-picker-group>
      </div>

      <combined-element class="fundingNeedsOtherPurposes" v-if="gotOtherReasons">
        <input-text
          id="funds-for-other-purpuses"
          v-model="application._extension.fundingPurposesOther"
          @input="checkFormValidation"
          :maxlength="FUNDING_PURPOSES_OTHER_REASON_MAX_LENGTH"
          v-validation="[['dynamic', '/_extension/fundingPurposesOther']]"
        >
          <template #label>
            <div class="fundingOtherReasonsTitle">{{ t('otherPlans.title') }}</div>
          </template>
          <div class="fundingOtherReasons" slot="description">
            <div class="fundingOtherReasonsDescription">
              {{ t('otherPlans.description') }}
            </div>
          </div>
        </input-text>
      </combined-element>

      <navigation-buttons
        :back="isBrokerFlow ? { label: 'Back', modifiers: { secondary: '' } } : false"
        @back="onBack"
        :next="{
          label: $t('nextButtonLabel'),
          modifiers: { disabled: !isFormValid },
        }"
        @next="submit"
      ></navigation-buttons>
    </form>
  </flow-layout>
</template>

<script>
import * as funds from '@/modules/funds';

import {
  FUNDING_PURPOSES_OTHER_REASON_MAX_LENGTH,
  FUNDING_NEEDS_DESIRED_LOAN_AMOUNT_MAX_LENGTH,
} from '@/modules/validation-service/fields-length';

import CombinedElement from '@/components/CombinedElement';
import FlowLayout from '@/views/layouts/FlowLayout';
import InputInline from '@/components/InputInline';
import applicationMixin from '@/mixins/application';
import brokerFlowMixin from '@/mixins/broker-flow';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import buildTranslators from '@/utils/build-translators';

export default {
  name: 'funding-needs',

  components: {
    FlowLayout,
    CombinedElement,
    InputInline,
  },

  data() {
    return {
      FUNDING_PURPOSES_OTHER_REASON_MAX_LENGTH,
      FUNDING_NEEDS_DESIRED_LOAN_AMOUNT_MAX_LENGTH,
      isFormValid: false,
    };
  },

  computed: {
    fundsOptions() {
      return mapConstsToOptions(Object.values(funds), 'fundingNeeds.fundings');
    },
    gotOtherReasons() {
      return (
        Array.isArray(this.application.fundingPurposes) &&
        this.application.fundingPurposes.includes(funds.OTHER)
      );
    },
  },

  mixins: [applicationMixin, brokerFlowMixin],

  methods: {
    onBack() {
      if (this.isBrokerFlow) {
        this.$router
          .replace({ name: 'BrokerDetails' + this.$route.matched[0].meta.product })
          .catch(() => {});
      } else {
        this.goBack();
      }
    },
    checkFormValidation() {
      this.$nextTick().then(() => {
        this.isFormValidFn();
      });
    },
    isFormValidFn() {
      let validationCounter = 0;
      this.isFormValid = false;
      this.$validations.collections[0].forEach((value, key) => {
        if (key === 'desired-loan-amount' && value.component.errorMessage === '') {
          validationCounter += 1;
        } else if (value.component.value.length > 0 && value.component.errorMessage === '') {
          validationCounter += 1;
        }
      });
      this.isFormValid = validationCounter === this.$validations.collections[0].size;
    },
    t(key, options) {
      return buildTranslators('fundingNeeds').t(key, options);
    },
  },
};
</script>

<style lang="sass" scoped>
.fundingNeeds
  h1
    margin-bottom: $glass-spacing-3
  .nextButton
    margin-top: $glass-spacing-4

.fundingNeedsTitle
  +font-size(16)
  margin-bottom: $glass-spacing-1
  color: $color-system-neutral-gray-6

.fundingNeedsSubtitle
  +font-size(13.33)
  margin: $glass-spacing-1 0
  color: $color-system-neutral-gray-5

.desiredLoanInput
  margin: 0

.fundingNeedsAmount
  margin-top: $glass-spacing-4

.fundingNeedsPurpose
  margin-top: $glass-spacing-4

#desired-loan-amount
  margin: $glass-spacing-3 0 $glass-spacing-3

.fundingSelectionCaption
  margin-top: $glass-spacing-1
  text-transform: none
  font-weight: normal
  font-style: italic
  line-height: 1.33
  color: red
  +font-size(13.33)

.fundingNeedsOtherPurposes
  margin-top: $glass-spacing-4
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  background-color: $color-system-neutral-white
  padding-left: $glass-spacing-0125

.fundingOtherReasonsTitle
  font-weight: bold

.fundingOtherReasons
  margin: $glass-spacing-105 0

.fundingOtherReasonsLabel
  +font-size(16)
</style>
